/* eslint-disable react/no-danger, react/no-unescaped-entities, max-len, no-template-curly-in-string */
import React from 'react';
import PropTypes from 'prop-types';
import track from 'react-tracking';
import { ModalDialog, ActionRow } from '@edx/paragon';

import { FormattedMessage } from 'gatsby-plugin-react-intl';
import { withLocation } from '@utils/navigation';
import withUser from '@utils/withUser';
import { submitEvent } from '@utils/analytics';
import { formatProgramPrice } from '@utils/formatting';
import { USER_SUBSCRIPTION_STATES } from '../../../../utils/constants';
import { useSubscriptionButton } from '../../../../source-utils/useSubscriptionButton';
import { getLmsLink } from '../../../../utils/edxLinks';

function ProgramSubscriptionModal(props) {
  const {
    subscriptionDetails,
    isOpen,
    closeModal,
    enrollButtonId,
    uuid,
  } = props;
  const { priceDetails, subscriptionStatus } = subscriptionDetails;
  const price = formatProgramPrice(priceDetails?.price);

  const {
    linkRef, id, hasId, linkUrl,
  } = useSubscriptionButton(props);
  const programDetailPageURL = getLmsLink(`/dashboard/programs/${uuid}`);

  const handleClickEvent = () => {
    const isTrialEligible = !(subscriptionStatus === USER_SUBSCRIPTION_STATES.ACTIVE || subscriptionStatus === USER_SUBSCRIPTION_STATES.IN_ACTIVE);
    const isNewSubscription = !(subscriptionStatus === USER_SUBSCRIPTION_STATES.ACTIVE || subscriptionStatus === USER_SUBSCRIPTION_STATES.IN_ACTIVE);

    const { tracking } = props;
    const getEventName = () => {
      let eventName;

      if (subscriptionStatus === USER_SUBSCRIPTION_STATES.ACTIVE) {
        eventName = 'edx.bi.user.subscription.program.click';
      } else if (subscriptionStatus === USER_SUBSCRIPTION_STATES.IN_ACTIVE) {
        eventName = 'edx.bi.user.subscription.program.restart.click';
      } else {
        eventName = 'edx.bi.user.subscription.program.start-trial.click';
      }

      return eventName;
    };

    const componentProps = {
      program_uuid: uuid,
      isTrialEligible,
      isNewSubscription,
      fromModal: true,
    };
    submitEvent(tracking, componentProps, getEventName());
  };

  const getButtonVariant = () => {
    if (subscriptionStatus === USER_SUBSCRIPTION_STATES.ACTIVE) {
      return (
        <a
          href={programDetailPageURL}
          className="enroll-btn btn btn-brand w-100"
          {...(hasId && { id })}
          ref={linkRef}
          onClick={handleClickEvent}
        >
          <FormattedMessage
            id="prospectus.program.header.subscription.modal.detail.button.title"
            defaultMessage="View program details"
            description="View program Details button"
          >{text => <div>{text}</div>}
          </FormattedMessage>
        </a>
      );
    } if (subscriptionStatus === USER_SUBSCRIPTION_STATES.IN_ACTIVE) {
      return (
        <a
          href={linkUrl}
          className="enroll-btn btn btn-brand w-100"
          {...(hasId && { id })}
          ref={linkRef}
          onClick={handleClickEvent}
        >
          <FormattedMessage
            id="prospectus.program.header.subscription.modal.restart.button.title"
            defaultMessage="Restart my subscription"
            description="Restart my subscription button"
          >{text => <div>{text}</div>}
          </FormattedMessage>
        </a>
      );
    }
    return (
      <a
        data-testid="enroll-now-button-id"
        href={linkUrl}
        className="enroll-btn btn btn-brand w-100"
        {...(hasId && { id })}
        ref={linkRef}
        onClick={handleClickEvent}
      >
        <FormattedMessage
          id="prospectus.program.header.subscription.modal.button.trial"
          defaultMessage="Enroll now"
          description="Trial version button"
        >{text => <div id={enrollButtonId}>{text}</div>}
        </FormattedMessage>
      </a>
    );
  };

  return (
    <ModalDialog
      title={'Here\'s how the program subscription works'}
      isOpen={isOpen}
      onClose={closeModal}
      hasCloseButton={false}
    >
      <ModalDialog.Header>
        <ModalDialog.Title>
          <FormattedMessage
            id="prospectus.program.header.subscription.modal.main.heading"
            description="Text for the Modal Heading"
            defaultMessage="Here's how the program subscription works"
          >{text => <div className="h3 mr-n3">{text}</div>}
          </FormattedMessage>
        </ModalDialog.Title>
      </ModalDialog.Header>

      <ModalDialog.Body>

        <FormattedMessage
          id="prospectus.program.header.subscription.modal.first.bullet.heading"
          description="Text for the first bullet heading"
          defaultMessage="Try out the program for free"
        >{text => <div>{text}</div>}
        </FormattedMessage>
        <ul>
          <FormattedMessage
            id="prospectus.program.header.subscription.modal.first.bullet.point"
            description="Content for first subscription bullet"
            defaultMessage="Start with a 7-day free trial — with full program access — to make sure it’s a good fit."
          >{text => <li className="mb-2">{text}</li>}
          </FormattedMessage>
        </ul>

        <FormattedMessage
          id="prospectus.program.header.subscription.modal.forth.bullet.heading"
          description="Text for the first bullet heading"
          defaultMessage="Progress at your own pace"
        >{text => <div>{text}</div>}
        </FormattedMessage>
        <ul>
          <FormattedMessage
            id="prospectus.program.header.subscription.modal.forth.bullet.point"
            description="Content for first subscription bullet"
            defaultMessage="Set your speed and effort level in self-paced courses."
          >{text => <li className="mb-2">{text}</li>}
          </FormattedMessage>
        </ul>
        <FormattedMessage
          id="prospectus.program.header.subscription.modal.second.bullet.heading"
          description="Text for the second bullet heading"
          defaultMessage="Pay monthly to continue full program access"
        >{text => <div>{text}</div>}
        </FormattedMessage>
        <ul>
          <FormattedMessage
            id="prospectus.program.header.subscription.modal.second.bullet.point"
            description="Content for second subscription bullet"
            defaultMessage="${price} {currency} per month after the free trial ends. "
            values={{ price, currency: priceDetails?.currency }}
          >{text => <li className="mb-2">{text}</li>}
          </FormattedMessage>
        </ul>
        <FormattedMessage
          id="prospectus.program.header.subscription.modal.third.bullet.heading"
          description="Text for the third bullet heading"
          defaultMessage="Cancel at any time"
        >{text => <div>{text}</div>}
        </FormattedMessage>
        <ul>
          <FormattedMessage
            id="prospectus.program.header.subscription.modal.third.bullet.point"
            description="Content for third subscription bullet"
            defaultMessage="No cancellation fees apply."
          >{text => <li>{text}</li>}
          </FormattedMessage>
        </ul>
      </ModalDialog.Body>

      <ModalDialog.Footer>
        <ActionRow isStacked>
          {getButtonVariant()}
          <ModalDialog.CloseButton variant="tertiary" onClick={closeModal}>
            <FormattedMessage
              id="prospectus.program.header.subscription.modal.dismiss.button"
              description="Dismiss button for modal"
              defaultMessage="Dismiss"
            />
          </ModalDialog.CloseButton>
        </ActionRow>
      </ModalDialog.Footer>

    </ModalDialog>
  );
}

ProgramSubscriptionModal.propTypes = {
  uuid: PropTypes.string.isRequired,
  subscriptionDetails: PropTypes.shape({
    isSubscriptionEnabled: PropTypes.bool,
    subscriptionStatus: PropTypes.string,
    // eslint-disable-next-line react/forbid-prop-types
    priceDetails: PropTypes.object,
  }).isRequired,
  isSubscriptionLoading: PropTypes.bool.isRequired,
  enrollButtonId: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  tracking: PropTypes.shape(),
};

ProgramSubscriptionModal.defaultProps = {
  isOpen: false,
  tracking: {},
  closeModal: () => { },
};

export default track({
  component: 'program-subscription-modal',
})(withUser(withLocation(ProgramSubscriptionModal)));
